<script>
import { GlProgressBar, GlLink, GlButton, GlTooltipDirective } from '@gitlab/ui';
import { __, n__, sprintf } from '~/locale';
import initIssuablePopovers from '~/issuable/popover';
import { MAX_MILESTONES_TO_DISPLAY } from '../constants';
import IssuableStats from './issuable_stats.vue';

export default {
  name: 'ReleaseBlockMilestoneInfo',
  components: {
    GlProgressBar,
    GlLink,
    GlButton,
    IssuableStats,
  },
  directives: {
    GlTooltip: GlTooltipDirective,
  },
  props: {
    milestones: {
      type: Array,
      required: true,
    },
    openedIssuesPath: {
      type: String,
      required: false,
      default: '',
    },
    closedIssuesPath: {
      type: String,
      required: false,
      default: '',
    },
    openedMergeRequestsPath: {
      type: String,
      required: false,
      default: '',
    },
    mergedMergeRequestsPath: {
      type: String,
      required: false,
      default: '',
    },
    closedMergeRequestsPath: {
      type: String,
      required: false,
      default: '',
    },
    showDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      showAllMilestones: false,
    };
  },
  computed: {
    percentCompleteText() {
      return sprintf(__('%{percent}%{percentSymbol} complete'), {
        percent: this.percentComplete,
        percentSymbol: '%',
      });
    },
    percentComplete() {
      const percent = Math.round((this.issueCounts.closed / this.issueCounts.total) * 100);
      return Number.isNaN(percent) ? 0 : percent;
    },
    issueCounts() {
      return this.milestones
        .map((m) => m.issueStats || {})
        .reduce(
          (acc, current) => {
            acc.total += current.total || 0;
            acc.closed += current.closed || 0;

            return acc;
          },
          {
            total: 0,
            closed: 0,
          },
        );
    },
    showMergeRequestStats() {
      return this.milestones.some((m) => m.mrStats);
    },
    mergeRequestCounts() {
      return this.milestones
        .map((m) => m.mrStats || {})
        .reduce(
          (acc, current) => {
            acc.total += current.total || 0;
            acc.merged += current.merged || 0;
            acc.closed += current.closed || 0;

            return acc;
          },
          {
            total: 0,
            merged: 0,
            closed: 0,
          },
        );
    },
    milestoneLabelText() {
      return n__('Milestone', 'Milestones', this.milestones.length);
    },
    milestonesToDisplay() {
      return this.showAllMilestones
        ? this.milestones
        : this.milestones.slice(0, MAX_MILESTONES_TO_DISPLAY);
    },
    showMoreLink() {
      return this.milestones.length > MAX_MILESTONES_TO_DISPLAY;
    },
    moreText() {
      return this.showAllMilestones
        ? __('show fewer')
        : sprintf(__('show %{count} more'), {
            count: this.milestones.length - MAX_MILESTONES_TO_DISPLAY,
          });
    },
  },
  watch: {
    showAllMilestones() {
      this.$nextTick(() => this.initMilestonePopovers());
    },
  },
  mounted() {
    this.initMilestonePopovers();
  },
  methods: {
    initMilestonePopovers() {
      const links = this.$el.querySelectorAll('[data-reference-type="milestone"]');
      initIssuablePopovers(Array.from(links));
    },
    toggleShowAll() {
      this.showAllMilestones = !this.showAllMilestones;
    },
    shouldRenderBullet(milestoneIndex) {
      return Boolean(milestoneIndex !== this.milestonesToDisplay.length - 1 || this.showMoreLink);
    },
    shouldRenderShowMoreLink(milestoneIndex) {
      return Boolean(milestoneIndex === this.milestonesToDisplay.length - 1 && this.showMoreLink);
    },
  },
};
</script>
<template>
  <div>
    <h3 class="gl-sr-only">
      {{ __('Milestone information') }}
    </h3>
    <div class="release-block-milestone-info gl-flex gl-flex-wrap gl-gap-6">
      <div class="js-milestone-list-container gl-mb-5 gl-flex gl-flex-col">
        <span class="gl-mb-2">{{ milestoneLabelText }}</span>
        <div class="gl-flex gl-flex-wrap gl-items-end">
          <template v-for="(milestone, index) in milestonesToDisplay">
            <gl-link
              :key="milestone.id"
              :href="milestone.webUrl"
              :data-milestone="milestone.id"
              :data-original="`%\&quot;${milestone.title}\&quot;`"
              data-reference-type="milestone"
              class="gfm gfm-milestone has-tooltip gl-mr-2"
              data-testid="milestone-title"
            >
              {{ milestone.title }}
            </gl-link>
            <template v-if="shouldRenderBullet(index)">
              <span :key="'bullet-' + milestone.id" class="gl-mr-2">&bull;</span>
            </template>
            <template v-if="shouldRenderShowMoreLink(index)">
              <gl-button :key="'more-button-' + milestone.id" variant="link" @click="toggleShowAll">
                {{ moreText }}
              </gl-button>
            </template>
          </template>
        </div>
      </div>
      <issuable-stats
        v-if="showDetails"
        :label="__('Issues')"
        :total="issueCounts.total"
        :closed="issueCounts.closed"
        :opened-path="openedIssuesPath"
        :closed-path="closedIssuesPath"
        data-testid="issue-stats"
      />
      <div
        v-if="showDetails"
        v-gl-tooltip
        class="gl-flex gl-min-h-9 gl-w-37 gl-flex-col"
        :title="__('Closed issues')"
        data-testid="milestone-progress-bar-container"
      >
        <span class="gl-mb-3">{{ percentCompleteText }}</span>
        <span class="gl-w-full">
          <gl-progress-bar :value="issueCounts.closed" :max="issueCounts.total" />
        </span>
      </div>
      <issuable-stats
        v-if="showMergeRequestStats"
        :label="__('Merge requests')"
        :total="mergeRequestCounts.total"
        :merged="mergeRequestCounts.merged"
        :closed="mergeRequestCounts.closed"
        :opened-path="openedMergeRequestsPath"
        :merged-path="mergedMergeRequestsPath"
        :closed-path="closedMergeRequestsPath"
        data-testid="merge-request-stats"
      />
    </div>
  </div>
</template>
